await themeResource(theme.resources['mc-shopify'], false);
await themeComponent(theme.components['mc-element'], false);
await themeResource(theme.resources['mc-utils'], false);

class McRecommended extends McElement {
  connectedCallback() {
    this.render();
  }

  render = async () => {
    const container = this.querySelector('[data-container]');        // <-- .swiper-wrapper
    const swiperEl  = this.querySelector('mc-swiper');
    const bpEl      = this.querySelector('mc-swiper-breakpoint');

    const sectionId = this.dataset.sectionId || 'helper-related';
    const productId = this.dataset.productId;
    const limit     = parseInt(this.dataset.limit, 10) || 10;
    const intent    = this.dataset.intent || 'related';

    // figure out how many slides are visible right now
    const visibleSlides = this._calcVisibleSlides(swiperEl, bpEl);
    const defaultSkels  = Math.min(limit, Math.max(1, visibleSlides * 2));

    // skeleton controls
    this.skelCount = parseInt(this.dataset.skeletonCount || `${defaultSkels}`, 10);
    this._skeletonNodes = [];
    this._skeletonTemplate = this._buildSkeletonTemplate(); // .swiper-slide

    if (!container || !productId || !sectionId) {
      console.warn('[mc-recommended] Missing required attributes:', { container, productId, sectionId });
      this.remove();
      return;
    }

    // paint skeletons that match current slides-per-view
    this.setAttribute('aria-busy', 'true');
    this._renderSkeletons(container);

    try {
      const products = await McShopify.getRecommendations({ product_id: productId, limit, intent });

      if (!products.length) {
        this._removeSkeletons();
        this.dispatchEvent(new CustomEvent('mc:recommended-ready', { bubbles: true, detail: { count: 0 } }));
        this.remove();
        return;
      }

      const fetchSection = (handle) =>
        new Promise((resolve, reject) => {
          McShopify.section({
            path: `${Shopify.routes.root}products/${handle}`,
            section_id: sectionId,
            onSuccess: resolve,
            onError: reject
          });
        });

      const results = await Promise.allSettled(products.map(p => fetchSection(p.handle)));
      const htmls = results.filter(r => r.status === 'fulfilled').map(r => r.value);

      // swap content in one go to avoid flicker
      container.innerHTML = htmls.join('');

      const count = container.children.length;

      if (count > 0) {
        this.hidden = false;
        this.setAttribute('data-ready', '1');
        // kick Swiper after DOM swap
        this.querySelector('mc-swiper')?.swiper?.update?.();
      }

      this.dispatchEvent(new CustomEvent('mc:recommended-ready', { bubbles: true, detail: { count } }));
    } catch (err) {
      console.error('[mc-recommended] Failed to load recommendations:', err);
      this.dispatchEvent(new CustomEvent('mc:recommended-ready', { bubbles: true, detail: { count: 0 } }));
      this.remove();
    } finally {
      this._removeSkeletons();
      this.removeAttribute('aria-busy');
    }
  };

  _calcVisibleSlides(swiperEl, bpEl) {
    // mobile default from mc-swiper
    const m = parseFloat(swiperEl?.dataset?.slides || '1') || 1;

    // desktop breakpoint + slides from mc-swiper-breakpoint
    const bp        = parseInt(bpEl?.dataset?.breakpoint || '0', 10) || 0;
    const d         = parseFloat(bpEl?.dataset?.slides || `${m}`) || m;
    const isDesktop = bp > 0 && window.innerWidth >= bp;

    return isDesktop ? d : m;
  }

  _buildSkeletonTemplate() {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide mc-skel';
    slide.setAttribute('data-skel', '1');
    // Keep aspect and spacing identical to real cards to avoid CLS
    slide.innerHTML = `
      <div class="product-card">
        <div class="card-image">
          <span class="lazy__uncollapser"></span>
        </div>
        <div style="height: 102px"></div>
      </div>
    `;
    return slide;
  }

  _renderSkeletons(container) {
    const frag = document.createDocumentFragment();
    this._skeletonNodes = [];
    const n = Math.max(0, this.skelCount);

    for (let i = 0; i < n; i++) {
      const node = this._skeletonTemplate.cloneNode(true);
      this._skeletonNodes.push(node);
      frag.appendChild(node);
    }

    container.appendChild(frag);
  }

  _removeSkeletons() {
    if (this._skeletonNodes?.length) {
      this._skeletonNodes.forEach(n => n?.remove());
      this._skeletonNodes = [];
    }
  }
}

if (!customElements.get('mc-recommended')) {
  window.customElements.define('mc-recommended', McRecommended);
}
window.McRecommended = McRecommended;
export default McRecommended;
